import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ContextService, Status } from '@bcef/ia-shared';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, of, pipe, switchMap, tap } from 'rxjs';

import { STEPS } from '@core/const';
import { buildEmailPayload } from '@core/converters/report-payload.converter';
import { UserProfile } from '@core/interfaces/user-profile.interface';
import { AttachmentService } from '@core/services';
import { EmailService } from '@core/services/email.service';
import { CustomerContext } from '@core/types/customer-context.type';

import { withConfig } from './features/with-config';
import { withContact } from './features/with-contact';
import { withDescription } from './features/with-description';
import { withDevice } from './features/with-device';
import { withLoadingStatus } from './features/with-loading-status';

interface ReportState {
  context: CustomerContext;
  currentStep: number;
  profile: UserProfile | null;
}

export const INITIAL_REPORT_STATE: ReportState = {
  context: 'guest',
  currentStep: 1,
  profile: null,
};

export const ReportStore = signalStore(
  { providedIn: 'root' },
  withState<ReportState>(INITIAL_REPORT_STATE),
  withLoadingStatus(),
  withConfig(),
  withDescription(),
  withDevice(),
  withContact(),
  withComputed((store) => ({
    isConnected: computed(() => store.context() === 'connected'),
    hidesContactIdentityFields: computed(() => store.description().theme === 'Espace invité'),
  })),
  withMethods(
    (
      store,
      attachmentService = inject(AttachmentService),
      emailService = inject(EmailService),
      router = inject(Router),
    ) => ({
      goToStep: (step: number): void => {
        patchState(store, { currentStep: step });
        router.navigate([`/${STEPS[step - 1]}`], { skipLocationChange: true });
        window.scrollTo({ top: 0 });
      },
      setContext: (context: CustomerContext): void => patchState(store, { context }),
      submit: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              loadingStatus: Status.PROCESSING,
            }),
          ),
          switchMap(() => {
            const payload = buildEmailPayload(
              store.context(),
              store.description(),
              store.device(),
              store.contact(),
            );
            return attachmentService
              .submit(payload, store.description().attachments, { connected: store.isConnected() })
              .pipe(
                tap(() => {
                  patchState(store, { loadingStatus: Status.SUCCESS, currentStep: 4 });
                  router.navigate([`/confirmation`], { skipLocationChange: true });
                  window.scrollTo({ top: 0 });
                }),
                catchError(() => {
                  patchState(store, {
                    loadingStatus: Status.FAIL,
                    currentStep: 5,
                  });
                  router.navigate([`/error`], { skipLocationChange: true });
                  window.scrollTo({ top: 0 });
                  return of(void 0);
                }),
              );
          }),
        ),
      ),
      loadEmail: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loadingStatus: Status.PROCESSING })),
          switchMap(() =>
            emailService.getEmail().pipe(
              tap((email) => {
                store.setEmail(email);
                patchState(store, { loadingStatus: Status.SUCCESS });
              }),
              catchError(() => {
                patchState(store, { loadingStatus: Status.FAIL });
                return of(null);
              }),
            ),
          ),
        ),
      ),
    }),
  ),
  withHooks({
    onInit(store, context = inject(ContextService)) {
      store.setContext(context.isConnectedUser ? 'connected' : 'guest');
      store.setClientType();
      if (context.isConnectedUser) {
        store.loadEmail();
        store.setClientInformation();
      }
    },
  }),
);

export type ReportStore = InstanceType<typeof ReportStore>;
